import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getBlogPost } from "../api";
import { useContent } from "../ContentContext";
import SEO from "./SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const { company } = useContent();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "var(--bone)" }}>
      {/* No noindex here on purpose: a client-side "not found" fallback can
          also trigger transiently during hydration if the /api/blog fetch is
          slow or fails in Googlebot's ~5s JS budget, which would incorrectly
          mark a real, existing article as noindex. If a slug truly does not
          exist, the site's normal indexation signals (no inbound links, no
          sitemap entry) already handle that — Google is not going to keep a
          404-like page in the index just because we forget a client-side
          noindex tag. */}
      <SEO title="Article not found" description="This tree service article was not found." path={`/blog/${slug}`} />
      <p style={{ color: "var(--pine)" }}>Article not found.</p>
      <Link to="/" className="font-semibold" style={{ color: "var(--brass)" }}>Back to home</Link>
    </div>
  );
  if (!post) return (
    <div className="min-h-screen" style={{ background: "var(--bone)" }}>
      {/* Loading placeholder — emit the slug-based SEO now so Googlebot never
          sees a route with the previous page's tags stuck around. */}
      <SEO title="Loading article…" description="Loading tree service article from Top of the Line Tree Services LLC." path={`/blog/${slug}`} />
    </div>
  );

  const desc = post.meta_description || post.excerpt || `${post.title} — tree service tips and insights from Top of the Line Tree Services LLC in Huntsville, AL.`;

  return (
    <article className="min-h-screen pb-24" style={{ background: "var(--bone)" }} data-testid="blog-post">
      <SEO
        title={post.title}
        description={desc}
        path={`/blog/${slug}`}
        image={post.cover}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          image: [post.cover],
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { "@type": "Organization", name: company.name },
          publisher: {
            "@type": "Organization",
            name: company.name,
            logo: { "@type": "ImageObject", url: "https://topofthelinetreeservicesllc.com/logo-top-of-the-line.png" },
          },
          mainEntityOfPage: `https://topofthelinetreeservicesllc.com/blog/${slug}`,
          description: desc,
        }}
      />
      <div className="relative h-[46vh] min-h-[320px] overflow-hidden">
        <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,31,22,0.95), rgba(10,31,22,0.3))" }} />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto w-full px-6 md:px-12 pb-10">
            <Link to="/" data-testid="blogpost-back" className="inline-flex items-center gap-2 mb-5 text-sm font-semibold" style={{ color: "var(--brass)" }}>
              <ArrowLeft size={16} /> Back to home
            </Link>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl leading-[0.95]" style={{ color: "var(--moss)" }}>
              {post.title}
            </h1>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-12">
        <div className="prose-content leading-relaxed" style={{ color: "#2a2a2a" }}
             dangerouslySetInnerHTML={{ __html: post.body }} />
        <div className="mt-12 p-8 rounded-2xl text-center" style={{ background: "var(--pine)" }}>
          <p className="font-display font-bold uppercase text-xl" style={{ color: "var(--moss)" }}>Need professional tree service?</p>
          <a href={`tel:${company.phoneRaw}`} className="mt-4 inline-block rounded-full px-8 py-3 font-semibold" style={{ background: "var(--sun)", color: "var(--pine)" }}>
            Call {company.phone}
          </a>
        </div>
      </div>
    </article>
  );
}
